import { Building2, CheckSquare, User, ChevronRight, Phone, Mail } from 'lucide-react';
import type {
  SearchPersonResult,
  SearchOrganizationResult,
  SearchTaskResult,
} from '../../types';
import { formatPhone } from '../../lib/formatPhone';

export function PersonRow({ person }: { person: SearchPersonResult }) {
  const name = `${person.firstName} ${person.lastName}`.trim();
  const phone = formatPhone(person.phone);
  return (
    <RowShell icon={<User className="w-4 h-4" />}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {[person.position, person.organization?.name].filter(Boolean).join(' · ') || 'Coach'}
        </div>
        {(phone || person.email) && (
          <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 min-w-0">
            {phone && (
              <span className="inline-flex items-center gap-1 flex-shrink-0">
                <Phone className="w-3 h-3 flex-shrink-0" />
                {phone}
              </span>
            )}
            {person.email && (
              <span className="inline-flex items-center gap-1 min-w-0">
                <Mail className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{person.email}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </RowShell>
  );
}

export function OrganizationRow({ org }: { org: SearchOrganizationResult }) {
  const location = [org.city, org.state].filter(Boolean).join(', ');
  return (
    <RowShell icon={<Building2 className="w-4 h-4" />}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{org.name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {[location, org.type === 'SCHOOL' ? 'School' : 'Club'].filter(Boolean).join(' · ')}
        </div>
      </div>
    </RowShell>
  );
}

export function TaskRow({ task }: { task: SearchTaskResult }) {
  const personName = task.person ? `${task.person.firstName} ${task.person.lastName}` : null;
  const due = task.dueDate ? new Date(task.dueDate).toLocaleDateString() : null;
  return (
    <RowShell icon={<CheckSquare className="w-4 h-4" />}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{task.title}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {[due, personName].filter(Boolean).join(' · ') || task.status}
        </div>
      </div>
    </RowShell>
  );
}

function RowShell({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-3 w-full px-4 py-2.5 min-h-[44px] text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 aria-selected:bg-gray-50 dark:aria-selected:bg-gray-700/50 transition-colors cursor-pointer"
    >
      <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">{icon}</span>
      {children}
      <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 md:hidden" />
    </div>
  );
}
